// import types
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type { BacklogTaskCategory } from './Backlog.types';

export type BacklogCategoryMeta = {
  category: BacklogTaskCategory;
  iconType: IconType;
  /**
   * False while the category has no backend behind it. Those tiles are still shown — the planner
   * should be able to see what is coming — but they are disabled and carry no count, because a
   * number there would be made up.
   */
  isImplemented: boolean;
};

/** Order follows the design. Only the first category is backed by data today. */
export const BACKLOG_CATEGORIES: BacklogCategoryMeta[] = [
  {
    category: 'deviation',
    iconType: 'arrow-down-arrow-up',
    isImplemented: true
  },
  {
    category: 'appointmentOverlap',
    iconType: 'layer-group',
    isImplemented: false
  },
  {
    category: 'functionMismatch',
    iconType: 'shield-check',
    isImplemented: false
  },
  { category: 'resourceMismatch', iconType: 'gear', isImplemented: false },
  {
    category: 'schedulingGap',
    iconType: 'calendar-xmark',
    isImplemented: false
  },
  { category: 'absenceConflict', iconType: 'user-xmark', isImplemented: false },
  { category: 'outsideWorkingHours', iconType: 'clock', isImplemented: false },
  // user-slash reads as "no employee" where user-minus would read as "remove one"; user-xmark is
  // already the absence tile.
  { category: 'missingEmployee', iconType: 'user-slash', isImplemented: false },
  // The treatment itself, as a care plan. Deliberately not heart-pulse-xmark, which is available and
  // fits "outside care" but reads as a patient dying in an application like this one.
  {
    category: 'outsideTreatment',
    iconType: 'clipboard-list',
    isImplemented: true
  },
  // Care receivers whose prescription is marked SPOED (urgent). Not backed yet, so it shows disabled
  // and countless until its backend lands.
  {
    category: 'spoed',
    iconType: 'triangle-exclamation',
    isImplemented: false
  },
  { category: 'syncIssue', iconType: 'arrows-rotate', isImplemented: false }
];

export const IMPLEMENTED_CATEGORIES: BacklogTaskCategory[] =
  BACKLOG_CATEGORIES.filter(({ isImplemented }) => isImplemented).map(
    ({ category }) => category
  );

export const ALL_TILE_ICON: IconType = 'grid-list';

/**
 * Categories whose rows start folded in. Tekort/Overschot can carry many deviating rules per care
 * receiver, so it opens on demand rather than filling the grid on first load; every other category
 * starts open.
 */
export const CATEGORIES_FOLDED_BY_DEFAULT: BacklogTaskCategory[] = [
  'deviation'
];

/**
 * localStorage key under which the takenlijst remembers its filter and view-settings popovers across
 * visits. Bump the `version` in Backlog.stores.ts if the persisted shape ever changes.
 */
export const BACKLOG_PREFERENCES_STORAGE_KEY = 'roster.backlogPreferences';
